/**
 * Logs de auditoria da finalizacao de pedido / envio de PDF por e-mail (XANO).
 *
 * Crie um endpoint POST no grupo `rEsr7KLt`:
 * Path sugerido: `logs/pedido_finalizacao`
 * Body: campos flatten + `occurred_at` e `app_env` preenchidos no envio.
 *
 * Ou importe columns de `docs/xano_pedido_finalizacao_logs.csv` como tabela.
 */
import { ENV, postJson } from '@/lib/apiConfig';

/** Path do endpoint POST no Xano (uma linha POST / registra uma etapa ou falha). */
export const XANO_PEDIDO_FINALIZACAO_LOG_PATH =
  'logs/pedido_finalizacao' as const;

export type PedidoFinalizeOutcome = 'ok' | 'error' | 'warn' | 'skip';

/** Passos esperados nos logs (texto livre no XANO; valores estaveis para filtro). */
export const PedidoFinalizeSteps = {
  FLOW_START: 'FINALIZE_FLOW_START',
  USER_RESOLVED: 'FINALIZE_USER_RESOLVED',
  PEDIDO_API_OK: 'FINALIZE_PEDIDO_API_OK',
  SF_OP_OK: 'FINALIZE_SF_OP_OK',
  SF_OP_FAIL: 'FINALIZE_SF_OP_FAIL',
  PDF_TRY: 'FINALIZE_PDF_EMAIL_TRY',
  PDF_ABORT_BLOCKED: 'PDF_ABORT_BLOCKED_GEN',
  PDF_BASE64_READY: 'PDF_BASE64_READY',
  PDF_BASE64_EMPTY: 'PDF_BASE64_EMPTY',
  EMAIL_USER_MISSING: 'EMAIL_USER_MISSING_SKIP',
  AUTH_ME_FETCH_FAIL: 'AUTH_ME_FETCH_FAIL',
  EMAIL_POST_OK: 'EMAIL_PEDIDO_POST_OK',
  EMAIL_POST_ATTEMPT: 'EMAIL_PEDIDO_POST_ATTEMPT',
  EMAIL_POST_FAIL: 'EMAIL_PEDIDO_POST_FAIL',
  SF_OP_EMAIL_FAIL_CONTINUED: 'FINALIZE_SF_OP_EMAIL_FAIL_CONTINUED',
  PDF_EMAIL_WRAPPER_FAIL: 'FINALIZE_PDF_EMAIL_WRAPPER_FAIL',
  PDF_GENERATION_FAIL: 'PDF_GENERATION_FAIL',
  FLOW_SUCCESS: 'FINALIZE_FLOW_UI_SUCCESS',
  FLOW_CATCH: 'FINALIZE_FLOW_CATCH',
} as const;

export interface PedidoFinalizeLogInput {
  correlation_id: string;
  step: string;
  outcome: PedidoFinalizeOutcome;
  pedido_id: number | null;
  /** IDs de carrinho separados por virgula (ex.: "12,34") */
  carrinho_ids?: string | null;
  user_id?: number | null;
  user_email_masked?: string | null;
  detail?: string | null;
  http_status?: number | null;
  /** Tamanho da string base64 do PDF (caracteres), sem prefixo data: */
  pdf_base64_length_chars?: number | null;
  items_count?: number | null;
  forma_pagamento?: string | null;
}

const DETAIL_MAX = 4000;

function trimDetail(s: string | null | undefined): string | null {
  if (s == null || s === '') return null;
  const t = String(s);
  return t.length > DETAIL_MAX ? `${t.slice(0, DETAIL_MAX)}...` : t;
}

/** Mascara e-mail para log (ex.: "gus***@dominio.com"). */
export function maskEmail(email: string | null | undefined): string | null {
  if (email == null || email === '') return null;
  const parts = String(email).trim().split('@');
  if (parts.length !== 2) return '***';
  const [user, domain] = parts;
  const vis = user.slice(0, Math.min(3, user.length));
  return `${vis}***@${domain}`;
}

function safeErrorDetail(err: unknown): string {
  if (err instanceof Error) {
    const st = (err as any).status;
    const extra = st != null ? ` [http ${st}]` : '';
    return `${err.message}${extra}`;
  }
  return String(err);
}

/**
 * Envia uma linha de log ao Xano (fire-and-forget seguro: nao relanca).
 * Pode falhar silenciosamente se o endpoint ainda nao existir (console.warn).
 */
export async function logPedidoFinalizacaoRow(
  row: PedidoFinalizeLogInput
): Promise<void> {
  const occurred_at = new Date().toISOString();
  const body = {
    ...row,
    detail: trimDetail(row.detail ?? null),
    occurred_at,
    app_env: ENV,
  };

  try {
    await postJson('rEsr7KLt', XANO_PEDIDO_FINALIZACAO_LOG_PATH, body);
  } catch (e) {
    console.warn('[pedidoFinalizeLog] falha ao enviar', body.step, e);
  }
}

/** Helper para extrair status HTTP de erros do apiFetch/postJson. */
export function httpStatusFromError(err: unknown): number | null {
  const s = (err as any)?.status;
  return typeof s === 'number' && Number.isFinite(s) ? s : null;
}

/** Contexto opcional para correlacionar logs do PDF + e-mail a finalizacao. */
export interface PedidoFinalizePdfLogCtx {
  correlationId: string;
  pedidoId: number;
  carrinhoIds: string;
  userId?: number;
  formaPagamento: string;
}

export { safeErrorDetail };